// @ts-nocheck
import { useState, useEffect } from 'react';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Checkbox } from './ui/checkbox';
import { ScrollArea } from './ui/scroll-area';
import { 
  ChevronDown, 
  ChevronRight,
  Layers,
  FileText,
  Zap,
  MousePointer,
  Search,
  X,
  Package
} from 'lucide-react';
import { supabase } from '@product-radar/shared';
import { useWorkspace } from '@/contexts/context';

interface Product {
  id: string;
  workspace_id: string;
  name: string;
  description: string | null;
  color: string;
  is_active: boolean;
}

interface ProductStructureItem {
  id: string;
  workspace_id: string;
  product_id: string | null;
  parent_id: string | null;
  type: 'module' | 'page' | 'feature' | 'element';
  name: string;
  description: string | null;
  sort_order: number;
  children?: ProductStructureItem[];
}

interface ProductNodeSelectorProps {
  selectedNodeIds: string[];
  onSelectionChange: (nodeIds: string[]) => void;
  label?: string;
  description?: string;
  allowProductSelection?: boolean;
}

const getTypeIcon = (type: string) => {
  switch (type) {
    case 'product': return <Package className="h-3 w-3" />;
    case 'module': return <Layers className="h-3 w-3" />;
    case 'page': return <FileText className="h-3 w-3" />;
    case 'feature': return <Zap className="h-3 w-3" />;
    case 'element': return <MousePointer className="h-3 w-3" />;
    default: return <FileText className="h-3 w-3" />;
  }
};

const getTypeBadge = (type: string) => {
  switch (type) {
    case 'product': return 'bg-indigo-100 text-indigo-700 border-indigo-200';
    case 'module': return 'bg-purple-100 text-purple-700 border-purple-200';
    case 'page': return 'bg-blue-100 text-blue-700 border-blue-200';
    case 'feature': return 'bg-green-100 text-green-700 border-green-200';
    case 'element': return 'bg-orange-100 text-orange-700 border-orange-200';
    default: return 'bg-gray-100 text-gray-700 border-gray-200';
  }
};

function buildHierarchy(items: ProductStructureItem[]): ProductStructureItem[] {
  const itemMap = new Map<string, ProductStructureItem>();
  const rootItems: ProductStructureItem[] = [];

  // First pass: create map
  items.forEach(item => {
    itemMap.set(item.id, { ...item, children: [] });
  });

  // Second pass: build hierarchy
  items.forEach(item => {
    const node = itemMap.get(item.id)!;
    if (item.parent_id) {
      const parent = itemMap.get(item.parent_id);
      if (parent) {
        parent.children = parent.children || [];
        parent.children.push(node);
      } else {
        rootItems.push(node);
      }
    } else {
      rootItems.push(node);
    }
  });

  return rootItems;
}

export function ProductNodeSelector({ 
  selectedNodeIds, 
  onSelectionChange,
  label = "Product Areas",
  description = "Select which parts of the product this relates to",
  allowProductSelection = true
}: ProductNodeSelectorProps) {
  const { workspace, membership } = useWorkspace();
  const [products, setProducts] = useState<Product[]>([]);
  const [items, setItems] = useState<ProductStructureItem[]>([]);
  const [expandedProducts, setExpandedProducts] = useState(new Set<string>());
  const [expandedItems, setExpandedItems] = useState(new Set<string>());
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [flatItems, setFlatItems] = useState<ProductStructureItem[]>([]);

  useEffect(() => {
    if (workspace) {
      loadProductStructure();
    }
  }, [workspace]);

  const loadProductStructure = async () => {
    if (!workspace) return;

    try {
      setLoading(true);
      
      // Load products
      const { data: productsData, error: productsError } = await supabase
        .from('products')
        .select('*')
        .eq('workspace_id', workspace.id)
        .eq('is_active', true)
        .order('sort_order', { ascending: true })
        .order('name', { ascending: true });

      if (productsError) throw productsError;
      
      // Load structure items
      const { data: itemsData, error: itemsError } = await supabase
        .from('product_structure_items')
        .select('*')
        .eq('workspace_id', workspace.id)
        .order('sort_order', { ascending: true });

      if (itemsError) throw itemsError;

      const hierarchical = buildHierarchy(itemsData || []);
      setProducts(productsData || []);
      setItems(hierarchical);
      setFlatItems(itemsData || []);
      
      // Auto-expand items that have selected children
      const toExpandItems = new Set<string>();
      const toExpandProducts = new Set<string>();
      
      itemsData?.forEach(item => {
        if (selectedNodeIds.includes(item.id)) {
          if (item.parent_id) {
            toExpandItems.add(item.parent_id);
          }
          if (item.product_id) {
            toExpandProducts.add(item.product_id);
          }
        }
      });
      
      // Also expand products if they're selected
      productsData?.forEach(product => {
        if (selectedNodeIds.includes(product.id)) {
          toExpandProducts.add(product.id);
        }
      });
      
      setExpandedItems(toExpandItems);
      setExpandedProducts(toExpandProducts);
    } catch (error) {
      console.error('Error loading product structure:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleProductExpanded = (id: string) => {
    const newExpanded = new Set(expandedProducts);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedProducts(newExpanded);
  };

  const toggleExpanded = (id: string) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedItems(newExpanded);
  };

  const toggleSelection = (id: string) => {
    const newSelection = selectedNodeIds.includes(id)
      ? selectedNodeIds.filter(nodeId => nodeId !== id)
      : [...selectedNodeIds, id];
    onSelectionChange(newSelection);
  };

  const removeSelection = (id: string) => {
    onSelectionChange(selectedNodeIds.filter(nodeId => nodeId !== id));
  };

  const getNodePath = (nodeId: string): string => {
    // Check if it's a product
    const product = products.find(p => p.id === nodeId);
    if (product) {
      return product.name;
    }
    
    // Otherwise it's a structure item
    const node = flatItems.find(item => item.id === nodeId);
    if (!node) return '';
    
    const path: string[] = [node.name];
    let currentParentId = node.parent_id;
    
    while (currentParentId) {
      const parent = flatItems.find(item => item.id === currentParentId);
      if (parent) {
        path.unshift(parent.name);
        currentParentId = parent.parent_id;
      } else {
        break;
      }
    }
    
    // Add product name at the beginning
    if (node.product_id) {
      const productForNode = products.find(p => p.id === node.product_id);
      if (productForNode) {
        path.unshift(productForNode.name);
      }
    }
    
    return path.join(' > ');
  };

  const getNodeType = (nodeId: string): string => {
    const product = products.find(p => p.id === nodeId);
    if (product) return 'product';
    
    const item = flatItems.find(i => i.id === nodeId);
    return item?.type || 'unknown';
  };

  const renderNode = (node: ProductStructureItem, level: number = 0) => {
    const hasChildren = node.children && node.children.length > 0;
    const isExpanded = expandedItems.has(node.id);
    const isSelected = selectedNodeIds.includes(node.id);

    if (searchQuery) {
      const matchesSearch = node.name.toLowerCase().includes(searchQuery.toLowerCase());
      if (!matchesSearch) return null;
    }

    return (
      <div key={node.id}>
        <div 
          className={`flex items-center space-x-2 py-2 px-2 rounded hover:bg-muted/50 transition-colors ${
            isSelected ? 'bg-primary/5' : ''
          }`}
          style={{ paddingLeft: `${level * 20 + 8}px` }}
        >
          {hasChildren ? (
            <button
              onClick={() => toggleExpanded(node.id)}
              className="p-0.5 hover:bg-muted rounded"
            >
              {isExpanded ? (
                <ChevronDown className="h-4 w-4 text-muted-foreground" />
              ) : (
                <ChevronRight className="h-4 w-4 text-muted-foreground" />
              )}
            </button>
          ) : (
            <div className="w-5" />
          )}
          
          <Checkbox
            checked={isSelected}
            onCheckedChange={() => toggleSelection(node.id)}
            id={`node-${node.id}`}
          />
          
          <label
            htmlFor={`node-${node.id}`}
            className="flex items-center space-x-2 flex-1 cursor-pointer"
          >
            <Badge variant="outline" className={`${getTypeBadge(node.type)} text-xs px-1.5 py-0.5`}>
              <div className="flex items-center space-x-1">
                {getTypeIcon(node.type)}
                <span className="capitalize">{node.type}</span>
              </div>
            </Badge>
            <span className="text-sm">{node.name}</span>
          </label>
        </div>
        
        {hasChildren && isExpanded && (
          <div>
            {node.children?.map(child => renderNode(child, level + 1))}
          </div>
        )}
      </div>
    );
  };

  const renderProduct = (product: Product) => {
    const productModules = items.filter(item => item.product_id === product.id && !item.parent_id);
    const isExpanded = expandedProducts.has(product.id);
    const isSelected = selectedNodeIds.includes(product.id);

    if (searchQuery) {
      const matchesSearch = product.name.toLowerCase().includes(searchQuery.toLowerCase());
      if (!matchesSearch) return null;
    }

    return (
      <div key={product.id}>
        <div 
          className={`flex items-center space-x-2 py-2 px-2 rounded hover:bg-muted/50 transition-colors ${
            isSelected ? 'bg-primary/5' : ''
          }`}
        >
          <button
            onClick={() => toggleProductExpanded(product.id)}
            className="p-0.5 hover:bg-muted rounded"
          >
            {isExpanded ? (
              <ChevronDown className="h-4 w-4 text-muted-foreground" />
            ) : (
              <ChevronRight className="h-4 w-4 text-muted-foreground" />
            )}
          </button>
          
          {allowProductSelection && (
            <Checkbox
              checked={isSelected}
              onCheckedChange={() => toggleSelection(product.id)}
              id={`product-${product.id}`}
            />
          )}
          
          <label
            htmlFor={allowProductSelection ? `product-${product.id}` : undefined}
            className={`flex items-center space-x-2 flex-1 ${allowProductSelection ? 'cursor-pointer' : ''}`}
          >
            <Badge variant="outline" className={`${getTypeBadge('product')} text-xs px-1.5 py-0.5`}>
              <div className="flex items-center space-x-1">
                <Package className="h-3 w-3" style={{ color: product.color }} />
                <span>Product</span>
              </div>
            </Badge>
            <span className="text-sm font-medium">{product.name}</span>
          </label>
        </div>
        
        {isExpanded && productModules.length > 0 && (
          <div>
            {productModules.map(module => renderNode(module, 1))}
          </div>
        )}
      </div>
    );
  };

  const selectedNodes = [
    ...products.filter(p => selectedNodeIds.includes(p.id)).map(p => ({ id: p.id, name: p.name, type: 'product' as const })),
    ...flatItems.filter(item => selectedNodeIds.includes(item.id)).map(i => ({ id: i.id, name: i.name, type: i.type }))
  ];

  return (
    <div className="space-y-3">
      <div className="space-y-1">
        <Label>{label}</Label>
        {description && (
          <p className="text-xs text-muted-foreground">{description}</p>
        )}
      </div>

      {/* Selected nodes display */}
      {selectedNodes.length > 0 && (
        <div className="flex flex-wrap gap-2 p-3 bg-muted/30 rounded-lg border">
          {selectedNodes.map(node => (
            <Badge 
              key={node.id} 
              variant="secondary"
              className="text-xs flex items-center space-x-1 pr-1"
            >
              {getTypeIcon(node.type)}
              <span className="ml-1">{getNodePath(node.id)}</span>
              <button
                onClick={() => removeSelection(node.id)}
                className="ml-1 hover:bg-muted rounded-full p-0.5"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search product areas..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-9"
        />
      </div>

      {/* Tree view */}
      <ScrollArea className="h-[300px] border rounded-lg p-2">
        {loading ? (
          <div className="flex items-center justify-center py-8 text-muted-foreground text-sm">
            Loading product structure...
          </div>
        ) : products.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-muted-foreground text-sm">
            <Package className="h-8 w-8 mb-2 opacity-50" />
            <p>No products defined yet</p>
            <p className="text-xs mt-1">Visit Product Definition to create one</p>
          </div>
        ) : (
          <div className="space-y-1">
            {products.map(product => renderProduct(product))}
          </div>
        )}
      </ScrollArea>

      {!loading && products.length > 0 && (
        <div className="flex items-center justify-between text-xs text-muted-foreground px-1">
          <span>{selectedNodes.length} selected</span>
          {selectedNodes.length > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="h-6 text-xs"
              onClick={() => onSelectionChange([])}
            >
              Clear all
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
